import { BottomSheet, Divider, Text, type UseModalState } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type TierType } from "@src/appV2/redesign/Tiers/api/schema";
import { type WorkerEventForRating } from "@src/appV2/redesign/Tiers/api/useWorkerEventsForRating";
import { IncreaseRatingTipsSection } from "@src/appV2/redesign/Tiers/RatingsSection/IncreaseRatingTipsSection";
import { WorkerEventsList } from "@src/appV2/redesign/Tiers/RatingsSection/WorkerEventsList";
import { type RatingType } from "@src/appV2/redesign/Tiers/utils/types";

interface RatingBottomSheetProps {
  modalState: UseModalState;
  ratingDisplay: string;
  workerEvents: WorkerEventForRating[];
  title: string;
  description: string;
  tips: string[];
  workerTimeZone: string;
  ratingType: RatingType;
  workerId: string;
  workerTier: TierType;
}

export function RatingBottomSheet(props: RatingBottomSheetProps) {
  const {
    modalState,
    ratingDisplay,
    workerEvents,
    title,
    description,
    tips,
    workerTimeZone,
    ratingType,
    workerId,
    workerTier,
  } = props;

  useLogEffect(APP_V2_USER_EVENTS.TIERS_RATING_BOTTOM_SHEET_VIEWED, {
    workerTier,
    workerId,
    ratingType,
  });

  return (
    <BottomSheet modalState={modalState} paperSx={{ ...getSafeAreaPaddings({ bottom: true }) }}>
      <IconButton
        size="medium"
        iconType="chevron-left"
        variant="outlined"
        aria-label="Close"
        sx={{ alignSelf: "flex-start", margin: 6, marginBottom: 4 }}
        onClick={() => {
          modalState.closeModal();
        }}
      />
      <DialogContent sx={{ paddingTop: 0 }}>
        <Stack spacing={0} alignItems="right">
          <Stack spacing={8} alignItems="left">
            <Stack spacing={2} alignItems="left">
              <Text semibold variant="h1">
                {ratingDisplay}
              </Text>
              <Stack spacing={4}>
                <Text semibold variant="h4">
                  {title}
                </Text>
                <Text variant="body1">{description}</Text>
                {ratingType === "shiftCompletionWithoutLateCancel" && (
                  <Text variant="body1">
                    No Show or Shift Abandonment will also count toward your late cancel tally. They
                    will result in a 30-day account suspension
                  </Text>
                )}
              </Stack>
            </Stack>
            <Divider />
            <IncreaseRatingTipsSection tips={tips} />
            <Divider />
            <WorkerEventsList workerEvents={workerEvents} workerTimeZone={workerTimeZone} />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
